import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Trash2 } from "lucide-react";
import { useApi, errMsg } from "@/lib/api";
import { PageHeader, Empty, StatusBadge } from "@/components/portal/StatusBadge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const when = (a) => `${new Date(`${a.date}T${a.time}`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })} · ${new Date(`${a.date}T${a.time}`).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`;

export default function Appointments() {
  const api = useApi();
  const [items, setItems] = useState(null);
  useEffect(() => {
    api("get", "/appointments").then(setItems);
  }, [api]);

  const setStatus = async (a, status) => {
    try {
      const na = await api("patch", `/appointments/${a.id}`, { status });
      setItems(items.map((x) => (x.id === a.id ? na : x)));
      if (na.email_sent === true) toast.success(`Visit confirmed — confirmation email sent to ${na.email}`);
      else if (na.email_sent === false) toast.warning("Visit confirmed, but the email could not be sent. Please contact the customer directly.");
      else toast.success(`Visit ${status}`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete visit request from ${a.name}?`)) return;
    try {
      await api("delete", `/appointments/${a.id}`);
      setItems(items.filter((x) => x.id !== a.id));
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div data-testid="appointments-page">
      <PageHeader eyebrow="Booked from the website" title="Estimate visits" />
      {items && !items.length && <Empty text="No visit requests yet." />}
      <div className="grid gap-4 md:grid-cols-2">
        {items?.map((a) => (
          <div key={a.id} className="rounded-xl border border-[#E6E2DA] bg-white p-5" data-testid={`appt-card-${a.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div><p className="font-display text-lg font-bold">{a.name}</p><p className="font-mono-num text-sm text-[#0E1F3F]">{when(a)}</p></div>
              <StatusBadge status={a.status} testId={`appt-status-${a.id}`} />
            </div>
            <p className="mt-1 text-xs capitalize text-[#8C877D]">{a.service.replace("_", " ")}</p>
            {a.confirmation_sent_at && <p className="mt-1 text-xs font-semibold text-green-700" data-testid={`appt-email-sent-${a.id}`}>Confirmation email sent</p>}
            {a.notes && <p className="mt-3 text-sm text-[#4A4742]">{a.notes}</p>}
            <div className="mt-3 space-y-1 text-sm">
              <a href={`mailto:${a.email}`} className="flex items-center gap-1.5 hover:text-[#A67C00]"><Mail size={14} />{a.email}</a>
              {a.phone && <a href={`tel:${a.phone}`} className="flex items-center gap-1.5 hover:text-[#A67C00]"><Phone size={14} />{a.phone}</a>}
              {a.address && <p className="flex items-center gap-1.5 text-[#5C5850]"><MapPin size={14} />{a.address}</p>}
            </div>
            <div className="mt-4 flex items-center gap-3">
              <div className="w-44">
              <Select value={a.status} onValueChange={(v) => setStatus(a, v)}>
                <SelectTrigger data-testid={`appt-status-select-${a.id}`} className="capitalize"><SelectValue /></SelectTrigger>
                <SelectContent>{["requested", "confirmed", "completed", "declined"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
              </Select>
              </div>
              <button onClick={() => remove(a)} data-testid={`appt-delete-${a.id}`} className="text-[#8C877D] hover:text-red-600" aria-label="Delete"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
